import { Camera, Package, Truck } from 'lucide-react'
import { useState } from 'react'

import { Business, useScanActions, useScanState } from '../stores/scanStore'

import HouseBillCard from './HouseBillCard'

import { useUIStore } from '@/stores/useUIStore'
import { HouseBillStatus } from '@/types/customs'
import {
  isDingTalkEnvironment,
  isMobile,
  scanQRCodeInDingTalk
} from '@/utils/dingtalk'

const ScanForm: React.FC = () => {
  const { showSuccess, showWarning, showInfo, showError } = useUIStore()
  const [inputValue, setInputValue] = useState('')
  const { scanning, houseBill, activeBusiness } = useScanState()
  const { houseBillScan, palletScan, toggleScanning } = useScanActions()

  const shouldShowDingTalkScan = isDingTalkEnvironment() && isMobile()

  const getThemeConfig = () => {
    const outboundTheme = {
      bg: 'bg-blue-50',
      border: 'border-blue-300',
      icon: 'text-blue-600',
      text: 'text-blue-700',
      inputBorder: 'border-blue-400',
      inputRing: 'focus:ring-blue-200',
      button: 'bg-blue-600 hover:bg-blue-700',
      title: '出库扫描',
      subtitle: '出库模式：扫描托盘进行出库标记',
      IconComponent: Package
    }

    const deliveryTheme = {
      bg: 'bg-orange-50',
      border: 'border-orange-300',
      icon: 'text-orange-600',
      text: 'text-orange-700',
      inputBorder: 'border-orange-400',
      inputRing: 'focus:ring-orange-200',
      button: 'bg-orange-600 hover:bg-orange-700',
      title: '发货扫描',
      subtitle: '发货模式：扫描托盘进行发货确认',
      IconComponent: Truck
    }

    // 优先根据 activeBusiness 来决定主题
    if (activeBusiness === Business.DELIVERY) {
      if (!houseBill) {
        return {
          ...deliveryTheme,
          subtitle: '请先扫描发货指令或托盘二维码'
        }
      }
      return deliveryTheme
    }

    // activeBusiness 是 OUTBOUND
    if (!houseBill) {
      return {
        ...outboundTheme,
        subtitle: '请先扫描出库单二维码加载托盘数据'
      }
    }
    return outboundTheme
  }

  const theme = getThemeConfig()
  const { IconComponent } = theme
  const isDeliveryFlow =
    activeBusiness === Business.DELIVERY ||
    (houseBill &&
      (houseBill.status === HouseBillStatus.PENDING_CONFIRMATION ||
        houseBill.status === HouseBillStatus.PENDING_DELIVERY))
  const flowLabel = isDeliveryFlow ? '发货' : '出库'

  const barcodeKeyDownHandle = (value: string) => {
    if (value === 'Enter') {
      handleInputSubmit(inputValue)
    }
  }

  const handleInputSubmit = async (value: string) => {
    const trimmedValue = value.trim()
    if (!trimmedValue) {
      showInfo(!houseBill ? `请输入${flowLabel}单号` : '请输入托盘编号')
      return
    }
    toggleScanning(true)
    try {
      await processScan(trimmedValue)
    } finally {
      toggleScanning(false)
    }
  }

  const handleDingTalkScan = async () => {
    if (scanning) return showWarning('请稍后, 上一次扫描任务还未结束')

    toggleScanning(true)
    try {
      const scanResult = await scanQRCodeInDingTalk()
      setInputValue(scanResult)
      showSuccess('扫码成功')
      handleInputSubmit(scanResult)
    } catch (error: unknown) {
      const errorMsg = error instanceof Error ? error.message : '扫码失败'
      if (errorMsg !== '用户取消扫描') {
        showError(errorMsg)
      }
    } finally {
      toggleScanning(false)
    }
  }

  const processScan = async (code: string) => {
    if (code.startsWith('PLT')) {
      if (!houseBill) {
        showInfo(`请先扫描${flowLabel}单`)
        return
      }
      await handlePalletScan(code)
    } else {
      await handleHouseBillScan(code)
    }
  }

  // 处理扫描到的出库单号
  const handleHouseBillScan = async (code: string) => {
    const result = await houseBillScan(code)
    if (result) {
      showSuccess(`已加载${flowLabel}单: ${code}`)
      setInputValue('')
    } else {
      showInfo(`未找到${flowLabel}单，请先扫描正确的单号`)
    }
  }

  const handlePalletScan = async (code: string) => {
    const { success, message } = await palletScan(code)
    if (success) {
      setInputValue('')
      showSuccess(message || '托盘扫描成功')
    } else {
      showInfo(message || `未找到托盘编号: ${code}`)
    }
  }

  return (
    <div className="w-full space-y-4">
      {/* 扫描输入卡片 */}
      <div
        className={`rounded-lg shadow-sm border-2 ${theme.bg} ${theme.border}`}
      >
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900 flex items-center">
            <IconComponent className={`w-6 h-6 mr-2 ${theme.icon}`} />
            <span>{theme.title}</span>
          </h2>
        </div>

        <div className="p-6 space-y-3">
          <div className="relative">
            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) =>
                barcodeKeyDownHandle(e.key)
              }
              placeholder={
                !houseBill
                  ? `扫描或输入${flowLabel}单号`
                  : '扫描托盘号码进行确认'
              }
              disabled={scanning}
              className={`w-full px-4 py-4 text-xl border-2 rounded-xl focus:outline-none transition-all duration-200 ${
                scanning
                  ? 'border-yellow-400 bg-yellow-100'
                  : inputValue.trim()
                  ? `${theme.inputBorder} bg-white focus:border-current focus:ring-4 ${theme.inputRing}`
                  : 'border-gray-300 bg-white focus:border-gray-400 focus:ring-4 focus:ring-gray-200'
              }`}
            />
          </div>

          {shouldShowDingTalkScan && (
            <button
              onClick={handleDingTalkScan}
              disabled={scanning}
              className={`w-full px-6 py-3 rounded-lg font-medium transition-all duration-200 flex items-center justify-center space-x-2 mx-auto ${
                scanning
                  ? 'bg-yellow-500 text-white cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl transform hover:-translate-y-0.5'
              }`}
            >
              {scanning ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  <span>扫描中...</span>
                </>
              ) : (
                <>
                  <Camera className="w-4 h-4" />
                  <span>调用摄像头扫描</span>
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {/* HouseBill 信息卡片 */}
      <HouseBillCard />
    </div>
  )
}

export default ScanForm
